export type Platform = 'ios-safari' | 'android-chrome' | 'desktop' | 'other';

/**
 * Coarse platform detection for the install-onboarding flow (DESIGN-BRIEF §4-A3): iOS
 * Safari and Android Chrome need different Add-to-Home-Screen instructions and iOS in
 * particular needs to be told that push requires installing first.
 */
export function detectPlatform(userAgent: string = navigator.userAgent): Platform {
  const ua = userAgent;
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as Mac but has touch support — treat it as iOS for install purposes.
  const isIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  const isIOS = isIOSDevice || isIPadOS;
  const isAndroid = /Android/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
  const isChrome = /Chrome/.test(ua) && !/Edg|OPR/.test(ua);

  if (isIOS && isSafari) return 'ios-safari';
  if (isIOS) return 'other'; // iOS Chrome/Firefox etc. still use WebKit but can't install
  if (isAndroid && isChrome) return 'android-chrome';
  if (isAndroid) return 'other';
  if (isChrome) return 'desktop';
  return 'other';
}

/** True when the app is already running installed/standalone (any platform). */
export function isStandalone(win: Window = window): boolean {
  const displayModeStandalone = win.matchMedia?.('(display-mode: standalone)').matches ?? false;
  // iOS Safari's own (non-standard) flag — `display-mode: standalone` isn't reliable there.
  const iosStandalone = (win.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return displayModeStandalone || iosStandalone;
}

export function supportsPush(win: Window = window): boolean {
  return 'serviceWorker' in win.navigator && 'PushManager' in win;
}

export function notificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}
